/**
 * Placeholder NIF registration data: option lists, per-step guidance and the
 * draft application. Replace the lookups with reference-data endpoints and the
 * draft with the application API.
 */

export const APPLICATION = {
  id: "APP-2026-000125",
  date: "01 Sep 2026 10:45 AM",
  status: "Draft",
  createdBy: "John Doe",
  updated: "01 Sep 2026 10:45 AM",
};

export const STEPS = [
  { key: "type", title: "Taxpayer Type", hint: "Select type" },
  { key: "basic", title: "Basic Information", hint: "Enter details" },
  { key: "address", title: "Address", hint: "Provide address" },
  { key: "other", title: "Other Information", hint: "Additional details" },
  { key: "review", title: "Review & Submit", hint: "Confirm & submit" },
];

export const TAXPAYER_TYPES = [
  {
    key: "legal",
    label: "Legal Entity",
    icon: "building",
    tone: "green",
    text: "Companies, organizations, institutions, and other legal persons.",
    detail:
      "This includes limited liability companies, partnerships, associations, foundations, and other incorporated or unincorporated organizations.",
    documents:
      "Certificate of Incorporation, Commercial Registration, Company Statutes.",
  },
  {
    key: "individual",
    label: "Individual",
    icon: "user",
    tone: "blue",
    text: "Natural persons and sole proprietors.",
    detail:
      "Natural persons registering in their own name, including sole proprietors trading under a business name.",
    documents: "National ID or Passport, Proof of Address, Tax Declaration.",
  },
  {
    key: "nonresident",
    label: "Non-Resident",
    icon: "users",
    tone: "indigo",
    text: "Non-resident individuals or entities.",
    detail:
      "Individuals or entities without a permanent establishment in Angola that have Angolan-source income.",
    documents:
      "Certificate of Tax Residence, Passport or Registration Certificate.",
  },
  {
    key: "government",
    label: "Government Entity",
    icon: "bank",
    tone: "amber",
    text: "Government bodies and public institutions.",
    detail:
      "Ministries, agencies, municipalities, and other public-sector bodies required to hold a NIF.",
    documents: "Establishing Decree, Public Institution Registration.",
  },
];

/* ------------------------------------------------------------- lookups -- */

export const LEGAL_STRUCTURES = [
  "Private Limited Company (Lda)",
  "Public Limited Company (SA)",
  "Sole Proprietorship",
  "Partnership",
  "Association",
  "Foundation",
];

export const FISCAL_YEAR_ENDS = [
  "31 December",
  "31 March",
  "30 June",
  "30 September",
];

export const BUSINESS_ACTIVITIES = [
  "Wholesale of other machinery and equipment",
  "Import and export of general goods",
  "Retail trade",
  "Construction",
  "Professional services",
  "Transport and storage",
];

export const COUNTRIES = ["Angola", "Portugal", "Namibia", "South Africa"];

export const PROVINCES = ["Luanda", "Bengo", "Huila", "Cabinda", "Namibe"];
export const MUNICIPALITIES = ["Luanda", "Belas", "Cacuaco", "Viana"];
export const DISTRICTS = ["Ingombota", "Maianga", "Rangel", "Samba"];
export const COMMUNES = ["Ingombota", "Maculusso", "Coqueiros"];

export const LEGAL_NATURES = [
  "Private Limited Company",
  "Public Limited Company",
  "Sole Proprietorship",
  "Cooperative",
];

export const EMPLOYEE_BANDS = ["1 - 10", "11 - 50", "51 - 200", "200+"];

export const INCOME_SOURCES = [
  "Trading of Goods",
  "Provision of Services",
  "Manufacturing",
  "Investment Income",
];

export const TURNOVER_BANDS = [
  "AOA 0 - 50,000,000",
  "AOA 50,000,001 - 200,000,000",
  "AOA 200,000,001 - 500,000,000",
  "AOA 500,000,001+",
];

export const CAPITAL_BANDS = [
  "AOA 0 - 10,000,000",
  "AOA 10,000,001 - 50,000,000",
  "AOA 50,000,001 - 100,000,000",
];

export const TAX_REGIMES = [
  "General Regime",
  "Simplified Regime",
  "Exemption Regime",
];

export const ACCOUNTING_METHODS = ["Accrual", "Cash"];

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const BANKS = [
  "Banco de Poupança e Crédito",
  "Banco Angolano de Investimentos",
  "Banco BIC",
  "Banco Millennium Atlântico",
];

export const BRANCHES = ["Luanda - Sede", "Luanda - Talatona", "Benguela"];

export const CAE_CODES = [
  "46520 - Wholesale of machinery",
  "46900 - Non-specialised wholesale trade",
  "41200 - Construction of buildings",
];

/* ---------------------------------------------------- per-step guidance -- */

export const GUIDELINES = {
  type: [
    { icon: "star", text: "Ensure all information is accurate and up to date." },
    { icon: "star", text: "Fields marked with * are mandatory." },
    { icon: "clock", text: "You can save as draft and complete later." },
    {
      icon: "shield",
      text: "After submission, application will be reviewed by DGI officials.",
    },
  ],
  basic: [
    { icon: "star", text: "Ensure all information is accurate and up to date." },
    { icon: "star", text: "Fields marked with * are mandatory." },
    { icon: "clock", text: "You can save as draft and complete later." },
    {
      icon: "shield",
      text: "After submission, application will be reviewed by DGI officials.",
    },
  ],
  address: [
    { icon: "info", text: "Provide the official registered address of the taxpayer." },
    {
      icon: "star",
      text: "The address will be used for legal and tax correspondence.",
    },
    { icon: "shield", text: "Ensure all address details are accurate and complete." },
    { icon: "mail", text: "You can add a correspondence address in the next step." },
  ],
  other: [
    { icon: "info", text: "Provide accurate business and tax related information." },
    {
      icon: "star",
      text: "This information helps DGI to assess your tax obligations.",
    },
    { icon: "mail", text: "You can save as draft and complete later." },
    {
      icon: "mail",
      text: "After submission, application will be reviewed by DGI officials.",
    },
  ],
  review: [
    { icon: "info", text: "Please review all information carefully before submission." },
    { icon: "info", text: "You can edit any section using the Edit buttons." },
    { icon: "info", text: "Once submitted, your application will be reviewed by DGI." },
    { icon: "info", text: "You will receive notifications about the status." },
  ],
};

export const CHECKLIST = [
  "All required information is provided",
  "Business and tax details look complete",
  "Address information is valid",
  "Ready to submit your application",
];

/** Required field names per step, used to gate the Next button. */
export const REQUIRED = {
  basic: [
    "legalStructure",
    "companyName",
    "registrationNumber",
    "incorporationDate",
    "primaryActivity",
    "countryIncorporation",
    "countryOperation",
    "telephone",
    "email",
    "description",
  ],
  address: [
    "country",
    "province",
    "municipality",
    "district",
    "commune",
    "neighborhood",
    "street",
    "building",
  ],
  other: [
    "legalNature",
    "incomeSource",
    "taxRegime",
    "accountingMethod",
    "financialYearStart",
    "caeCode",
  ],
};

/** Values the mockup ships with, so the wizard opens populated. */
export const INITIAL_FORM = {
  taxpayerType: "legal",

  legalStructure: "Private Limited Company (Lda)",
  companyName: "Global Imports SARL",
  tradeName: "Global Imports",
  registrationNumber: "APP-2026-000125",
  incorporationDate: "2026-09-01",
  fiscalYearEnd: "31 December",
  primaryActivity: "Wholesale of other machinery and equipment",
  secondaryActivity: "Import and export of general goods",
  countryIncorporation: "Angola",
  countryOperation: "Angola",
  telephone: "+244 222 70 60 00",
  email: "info@globalimports.ao",
  website: "www.globalimports.ao",
  description:
    "Global Imports SARL is engaged in the wholesale, import, and distribution of industrial machinery and equipment across Angola.",

  addressType: "registered",
  country: "Angola",
  province: "Luanda",
  municipality: "Luanda",
  district: "Ingombota",
  commune: "Ingombota",
  neighborhood: "Maculusso",
  street: "Avenida 4 de Fevereiro",
  building: "123",
  floor: "5",
  apartment: "5B",
  poBox: "456",
  postalCode: "1110",
  landmark: "Near Ministério das Finanças",
  addressDescription: "",

  businessStarted: "2020-09-01",
  legalNature: "Private Limited Company",
  employees: "11 - 50",
  incomeSource: "Trading of Goods",
  turnover: "AOA 50,000,001 - 200,000,000",
  capital: "AOA 10,000,001 - 50,000,000",
  taxRegime: "General Regime",
  vatRequired: "yes",
  exciseApplicable: "no",
  accountingMethod: "Accrual",
  financialYearStart: "January",
  bankName: "Banco de Poupança e Crédito",
  branch: "Luanda - Sede",
  iban: "AO06 0051 0000 1234 5678 9010 1",
  caeCode: "46520 - Wholesale of machinery",
  businessWebsite: "www.globalimports.ao",
  notes: "Importer and distributor of industrial machinery and equipment.",

  declaration: true,
};

export const RECENT_APPLICATIONS = [
  {
    id: "APP-2026-000125",
    name: "Global Imports SARL",
    type: "Legal Entity",
    created: "01 Sep 2026",
    status: "Approved",
  },
  {
    id: "APP-2026-000124",
    name: "Maria Fernandes",
    type: "Individual",
    created: "31 Aug 2026",
    status: "Under Review",
  },
  {
    id: "APP-2026-000123",
    name: "Angotech Solutions, Lda",
    type: "Legal Entity",
    created: "31 Aug 2026",
    status: "Draft",
  },
  {
    id: "APP-2026-000122",
    name: "João Manuel",
    type: "Individual",
    created: "31 Aug 2026",
    status: "Rejected",
  },
  {
    id: "APP-2026-000121",
    name: "Ministério das Finanças",
    type: "Government Entity",
    created: "30 Aug 2026",
    status: "Approved",
  },
];
